import { useEffect, useState, useRef } from 'react';
import { TextField, Typography, Box, Button, CircularProgress, Chip, Grid, Paper } from '@material-ui/core';
import Alert from '@material-ui/lab/Alert';
import { baseEndpoint } from '../App';
import { loadMapApi } from '../PropertyList/PropertyList';
const config = require("../env.json");

interface LocationStepProps {
    address: string;
    city: string;
    state: string;
    zip: string;
    onBoroughChange: (event: React.ChangeEvent<HTMLInputElement>) => void;
    onBlockChange: (event: React.ChangeEvent<HTMLInputElement>) => void;
    onLotChange: (event: React.ChangeEvent<HTMLInputElement>) => void;
    currentBorough: string;
    currentBlock: string;
    currentLot: string;
    boroughError: boolean;
    blockError: boolean;
    lotError: boolean;
    // New props for coordinates
    existingLat?: number | null;
    existingLng?: number | null;
    existingStreetLat?: number | null;
    existingStreetLng?: number | null;
    existingHeading?: number;
    onLatChange: (lat: number | null) => void;
    onLngChange: (lng: number | null) => void;
    onStreetLatChange: (streetLat: number | null) => void;
    onStreetLngChange: (streetLng: number | null) => void;
    onHeadingChange: (heading: number) => void;
}

interface LocationSuggestion {
    bbl: string;
    block: string;
    boroughCode: string;
    lot: string;
    lat?: number | null;
    lng?: number | null;
}

//type GoogleLatLng = google.maps.LatLng;
type GoogleMap = google.maps.Map;

export default function LocationStep(props: LocationStepProps) {
    const [loading, setLoading] = useState(false);
    const [error, setError] = useState<string | null>(null);
    const [suggestions, setSuggestions] = useState<LocationSuggestion | null>(null);
    const [scriptLoaded, setScriptLoaded] = useState(false);
    const [map, setMap] = useState<GoogleMap>();
    const markerRef = useRef<google.maps.Marker | null>(null);
    const mapRef = useRef<HTMLDivElement>(null);
    
    // State for coordinates
    const [mapPinLat, setMapPinLat] = useState<string>(props.existingLat?.toString() || "");
    const [mapPinLng, setMapPinLng] = useState<string>(props.existingLng?.toString() || "");
    const [streetViewLat, setStreetViewLat] = useState<string>(props.existingStreetLat?.toString() || props.existingLat?.toString() || "");
    const [streetViewLng, setStreetViewLng] = useState<string>(props.existingStreetLng?.toString() || props.existingLng?.toString() || "");
    const [heading, setHeading] = useState<string>(props.existingHeading?.toString() || "90");
    
    const [suggestedLat, setSuggestedLat] = useState<number | null>(null);
    const [suggestedLng, setSuggestedLng] = useState<number | null>(null);
    
    const isEditMode = props.existingLat != null && props.existingLng != null;

    // Sync coordinates when props change (for edit mode)
    useEffect(() => {
        if (props.existingLat != null && props.existingLng != null) {
            setMapPinLat(props.existingLat.toString());
            setMapPinLng(props.existingLng.toString());
            setStreetViewLat(props.existingStreetLat?.toString() || props.existingLat.toString());
            setStreetViewLng(props.existingStreetLng?.toString() || props.existingLng.toString());
            setHeading(props.existingHeading?.toString() || "90");
        }
    }, [props.existingLat, props.existingLng, props.existingStreetLat, props.existingStreetLng, props.existingHeading]);

    // Fetch location suggestions when address is available
    useEffect(() => {
        const fetchLocationSuggestions = async () => {
            if (!props.address || !props.city || !props.state || !props.zip) {
                return;
            }
            
            setLoading(true);
            setError(null);
            
            try {
                // Parse the address to extract house number and street
                const addressParts = props.address.trim().split(' ');
                const houseNumber = addressParts[0] || '';
                const street = addressParts.slice(1).join(' ') || '';
                
                if (!houseNumber || !street) {
                    setError('Please enter a complete address with house number and street name');
                    return;
                }
                
                const encodedHouseNumber = encodeURIComponent(houseNumber);
                const encodedStreet = encodeURIComponent(street);
                const encodedZip = encodeURIComponent(props.zip);
                
                const response = await fetch(
                    `${baseEndpoint}datageneration/bbl?houseNumber=${encodedHouseNumber}&street=${encodedStreet}&zip=${encodedZip}`
                );
                
                if (response.ok) {
                    const data = await response.json();
                    // Extract location data from the API response
                    const locationData: LocationSuggestion = {
                        bbl: data.bbl || '',
                        block: data.block || '',
                        boroughCode: data.boroughCode || '',
                        lot: data.lot || '',
                        lat: data.lat || null,
                        lng: data.lng || null
                    };
                    setSuggestions(locationData);
                    
                    // If we have lat/lng from API, set suggested coordinates
                    if (data.lat != null && data.lng != null) {
                        setSuggestedLat(data.lat);
                        setSuggestedLng(data.lng);
                        
                        // For new properties, initialize coordinates with API values
                        if (!isEditMode) {
                            setMapPinLat(data.lat.toString());
                            setMapPinLng(data.lng.toString());
                            setStreetViewLat(data.lat.toString());
                            setStreetViewLng(data.lng.toString());
                            props.onLatChange(data.lat);
                            props.onLngChange(data.lng);
                            props.onStreetLatChange(data.lat);
                            props.onStreetLngChange(data.lng);
                        }
                    }
                } else {
                    setError(`Failed to fetch location suggestions: ${response.status}`);
                }
            } catch (ex) {
                setError('Failed to fetch location suggestions');
                console.error('Error fetching location suggestions:', ex);
            } finally {
                setLoading(false);
            }
        };

        fetchLocationSuggestions();
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [props.address, props.city, props.state, props.zip]);

    // Initialize map when script is loaded
    useEffect(() => {
        // Check if Google Maps API is available (directly check window, don't rely on scriptLoaded state)
        const isGoogleMapsAvailable = typeof window !== 'undefined' && window.google && window.google.maps && window.google.maps.Map;
        
        if (!map && mapRef.current) {
            if (isGoogleMapsAvailable) {
                try {
                    // Determine center coordinates
                    const lat = mapPinLat ? parseFloat(mapPinLat) : (suggestedLat || null);
                    const lng = mapPinLng ? parseFloat(mapPinLng) : (suggestedLng || null);
                    
                    // Use coordinates if available, otherwise use default NYC center
                    const centerLat = (lat != null && !isNaN(lat)) ? lat : 40.7128;
                    const centerLng = (lng != null && !isNaN(lng)) ? lng : -74.0060;
                    
                    const center = new google.maps.LatLng(centerLat, centerLng);
                    const newMap = new google.maps.Map(mapRef.current, {
                        zoom: lat != null && lng != null ? 18 : 12,
                        center: center,
                        mapTypeControl: false,
                        streetViewControl: false,
                        rotateControl: false,
                        scaleControl: true,
                        fullscreenControl: false,
                        panControl: false,
                        zoomControl: true,
                        gestureHandling: 'cooperative',
                        mapTypeId: google.maps.MapTypeId.ROADMAP,
                        styles: [
                            {
                                featureType: "poi",
                                stylers: [{ visibility: "off" }]
                            },
                            {
                                featureType: "transit",
                                stylers: [{ visibility: "off" }]
                            }
                        ]
                    });
                    
                    // Update marker when map is clicked
                    newMap.addListener('click', (e: google.maps.MapMouseEvent) => {
                        if (e.latLng) {
                            const newLat = e.latLng.lat();
                            const newLng = e.latLng.lng();
                            
                            if (!markerRef.current) {
                                // Create marker if it doesn't exist
                                const newMarker = new google.maps.Marker({
                                    position: e.latLng,
                                    map: newMap,
                                    icon: '../green-pin.png',
                                    draggable: true
                                });
                                
                                newMarker.addListener('dragend', () => {
                                    const position = newMarker.getPosition();
                                    if (position) {
                                        const dragLat = position.lat();
                                        const dragLng = position.lng();
                                        setMapPinLat(dragLat.toString());
                                        setMapPinLng(dragLng.toString());
                                        props.onLatChange(dragLat);
                                        props.onLngChange(dragLng);
                                    }
                                });
                                
                                markerRef.current = newMarker;
                            } else {
                                markerRef.current.setPosition(e.latLng);
                            }
                            
                            setMapPinLat(newLat.toString());
                            setMapPinLng(newLng.toString());
                            props.onLatChange(newLat);
                            props.onLngChange(newLng);
                        }
                    });
                    
                    setMap(newMap);
                } catch (error) {
                    console.error('Error initializing map:', error);
                }
            } else {
                // Google Maps not available yet, set up a polling mechanism
                const checkInterval = setInterval(() => {
                    if (typeof window !== 'undefined' && window.google && window.google.maps && window.google.maps.Map && mapRef.current) {
                        clearInterval(checkInterval);
                        // Re-trigger the effect by updating a dependency
                        // We'll use a small delay to ensure the DOM is ready
                        setTimeout(() => {
                            const lat = mapPinLat ? parseFloat(mapPinLat) : (suggestedLat || null);
                            const lng = mapPinLng ? parseFloat(mapPinLng) : (suggestedLng || null);
                            const centerLat = (lat != null && !isNaN(lat)) ? lat : 40.7128;
                            const centerLng = (lng != null && !isNaN(lng)) ? lng : -74.0060;
                            
                            try {
                                const center = new google.maps.LatLng(centerLat, centerLng);
                                const newMap = new google.maps.Map(mapRef.current!, {
                                    zoom: lat != null && lng != null ? 18 : 12,
                                    center: center,
                                    mapTypeControl: false,
                                    streetViewControl: false,
                                    rotateControl: false,
                                    scaleControl: true,
                                    fullscreenControl: false,
                                    panControl: false,
                                    zoomControl: true,
                                    gestureHandling: 'cooperative',
                                    mapTypeId: google.maps.MapTypeId.ROADMAP,
                                    styles: [
                                        { featureType: "poi", stylers: [{ visibility: "off" }] },
                                        { featureType: "transit", stylers: [{ visibility: "off" }] }
                                    ]
                                });
                                
                                newMap.addListener('click', (e: google.maps.MapMouseEvent) => {
                                    if (e.latLng) {
                                        const newLat = e.latLng.lat();
                                        const newLng = e.latLng.lng();
                                        
                                        if (!markerRef.current) {
                                            const newMarker = new google.maps.Marker({
                                                position: e.latLng,
                                                map: newMap,
                                                icon: '../green-pin.png',
                                                draggable: true
                                            });
                                            
                                            newMarker.addListener('dragend', () => {
                                                const position = newMarker.getPosition();
                                                if (position) {
                                                    const dragLat = position.lat();
                                                    const dragLng = position.lng();
                                                    setMapPinLat(dragLat.toString());
                                                    setMapPinLng(dragLng.toString());
                                                    props.onLatChange(dragLat);
                                                    props.onLngChange(dragLng);
                                                }
                                            });
                                            
                                            markerRef.current = newMarker;
                                        } else {
                                            markerRef.current.setPosition(e.latLng);
                                        }
                                        
                                        setMapPinLat(newLat.toString());
                                        setMapPinLng(newLng.toString());
                                        props.onLatChange(newLat);
                                        props.onLngChange(newLng);
                                    }
                                });
                                
                                setMap(newMap);
                            } catch (error) {
                                console.error('Error initializing map (delayed):', error);
                            }
                        }, 100);
                    }
                }, 100);
                
                // Cleanup after 10 seconds
                setTimeout(() => clearInterval(checkInterval), 10000);
                
                return () => clearInterval(checkInterval);
            }
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [map, mapPinLat, mapPinLng, suggestedLat, suggestedLng]);

    // Update marker position when coordinates change manually or when coordinates arrive
    useEffect(() => {
        if (map && mapPinLat && mapPinLng) {
            const lat = parseFloat(mapPinLat);
            const lng = parseFloat(mapPinLng);
            
            if (!isNaN(lat) && !isNaN(lng)) {
                const position = new google.maps.LatLng(lat, lng);
                
                if (markerRef.current) {
                    markerRef.current.setPosition(position);
                } else {
                    // Create marker if it doesn't exist yet
                    const newMarker = new google.maps.Marker({
                        position: position,
                        map: map,
                        icon: '../green-pin.png',
                        draggable: true
                    });
                    
                    newMarker.addListener('dragend', () => {
                        const pos = newMarker.getPosition();
                        if (pos) {
                            const newLat = pos.lat();
                            const newLng = pos.lng();
                            setMapPinLat(newLat.toString());
                            setMapPinLng(newLng.toString());
                            props.onLatChange(newLat);
                            props.onLngChange(newLng);
                        }
                    });
                    
                    markerRef.current = newMarker;
                }
                
                map.setCenter(position);
            }
        } else if (map && suggestedLat != null && suggestedLng != null && !mapPinLat && !mapPinLng) {
            // If we have suggested coordinates but no current ones, center map on suggested
            const position = new google.maps.LatLng(suggestedLat, suggestedLng);
            map.setCenter(position);
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [mapPinLat, mapPinLng, map, suggestedLat, suggestedLng]);

    // Load Google Maps API and detect when it's ready
    useEffect(() => {
        // Check if Google Maps is already loaded
        if (typeof window !== 'undefined' && window.google && window.google.maps && window.google.maps.Map) {
            if (!scriptLoaded) {
                setScriptLoaded(true);
            }
            return;
        }
        
        // Load the script if not already loaded
        if (!scriptLoaded) {
            // eslint-disable-next-line @typescript-eslint/no-use-before-define
            const googleMapScript = loadMapApi();
            
            // Check if script already existed (was already loaded)
            if (googleMapScript.src && googleMapScript.src.indexOf('maps.googleapis.com') !== -1) {
                // Script already exists, check if it's loaded
                const checkGoogle = setInterval(() => {
                    if (typeof window !== 'undefined' && window.google && window.google.maps && window.google.maps.Map) {
                        setScriptLoaded(true);
                        clearInterval(checkGoogle);
                    }
                }, 100);
                
                // Cleanup after 10 seconds
                setTimeout(() => clearInterval(checkGoogle), 10000);
            } else {
                // New script, wait for load event
                googleMapScript.addEventListener('load', () => {
                    setScriptLoaded(true);
                });
            }
        }
    }, [scriptLoaded]);
    
    // Watch for Google Maps to become available (in case script loads asynchronously)
    useEffect(() => {
        if (!scriptLoaded && typeof window !== 'undefined') {
            const checkInterval = setInterval(() => {
                if (window.google && window.google.maps && window.google.maps.Map) {
                    setScriptLoaded(true);
                    clearInterval(checkInterval);
                }
            }, 100);
            
            return () => clearInterval(checkInterval);
        }
    }, [scriptLoaded]);

    const handleApplySuggestion = (field: 'boroughCode' | 'block' | 'lot') => {
        if (!suggestions) return;

        const mockEvent = {
            target: {
                value: suggestions[field].toString()
            }
        } as React.ChangeEvent<HTMLInputElement>;

        switch (field) {
            case 'boroughCode':
                props.onBoroughChange(mockEvent);
                break;
            case 'block':
                props.onBlockChange(mockEvent);
                break;
            case 'lot':
                props.onLotChange(mockEvent);
                break;
        }
    };

    const handleUseSuggestedCoordinates = () => {
        if (suggestedLat != null && suggestedLng != null) {
            setMapPinLat(suggestedLat.toString());
            setMapPinLng(suggestedLng.toString());
            setStreetViewLat(suggestedLat.toString());
            setStreetViewLng(suggestedLng.toString());
            props.onLatChange(suggestedLat);
            props.onLngChange(suggestedLng);
            props.onStreetLatChange(suggestedLat);
            props.onStreetLngChange(suggestedLng);
        }
    };

    const handleSyncToMapPin = () => {
        if (mapPinLat && mapPinLng) {
            setStreetViewLat(mapPinLat);
            setStreetViewLng(mapPinLng);
            props.onStreetLatChange(parseFloat(mapPinLat));
            props.onStreetLngChange(parseFloat(mapPinLng));
        }
    };

    // Helper function to parse pasted coordinates (e.g., "38.325123401088184, -75.60647171905906")
    const parseCoordinatePair = (value: string): { lat: string | null; lng: string | null } | null => {
        // Check if it looks like a coordinate pair (contains comma)
        const trimmed = value.trim();
        if (trimmed.includes(',')) {
            const parts = trimmed.split(',').map(p => p.trim());
            if (parts.length === 2) {
                const lat = parseFloat(parts[0]);
                const lng = parseFloat(parts[1]);
                if (!isNaN(lat) && !isNaN(lng)) {
                    return { lat: parts[0], lng: parts[1] };
                }
            }
        }
        return null;
    };

    const handleMapPinChange = (field: 'lat' | 'lng', value: string) => {
        if (field === 'lat') {
            setMapPinLat(value);
            const numValue = value === "" ? null : parseFloat(value);
            props.onLatChange(isNaN(numValue as number) ? null : numValue);
        } else {
            setMapPinLng(value);
            const numValue = value === "" ? null : parseFloat(value);
            props.onLngChange(isNaN(numValue as number) ? null : numValue);
        }
    };

    const handleMapPinPaste = (e: React.ClipboardEvent, field: 'lat' | 'lng') => {
        const pastedText = e.clipboardData.getData('text');
        const parsed = parseCoordinatePair(pastedText);
        
        if (parsed) {
            e.preventDefault();
            setMapPinLat(parsed.lat || "");
            setMapPinLng(parsed.lng || "");
            if (parsed.lat) {
                const latNum = parseFloat(parsed.lat);
                props.onLatChange(isNaN(latNum) ? null : latNum);
            }
            if (parsed.lng) {
                const lngNum = parseFloat(parsed.lng);
                props.onLngChange(isNaN(lngNum) ? null : lngNum);
            }
        }
    };

    const handleStreetViewChange = (field: 'lat' | 'lng' | 'heading', value: string) => {
        if (field === 'lat') {
            setStreetViewLat(value);
            const numValue = value === "" ? null : parseFloat(value);
            props.onStreetLatChange(isNaN(numValue as number) ? null : numValue);
        } else if (field === 'lng') {
            setStreetViewLng(value);
            const numValue = value === "" ? null : parseFloat(value);
            props.onStreetLngChange(isNaN(numValue as number) ? null : numValue);
        } else {
            setHeading(value);
            const numValue = parseFloat(value);
            props.onHeadingChange(isNaN(numValue) ? 90 : numValue);
        }
    };

    const handleStreetViewPaste = (e: React.ClipboardEvent, field: 'lat' | 'lng') => {
        const pastedText = e.clipboardData.getData('text');
        const parsed = parseCoordinatePair(pastedText);
        
        if (parsed) {
            e.preventDefault();
            setStreetViewLat(parsed.lat || "");
            setStreetViewLng(parsed.lng || "");
            if (parsed.lat) {
                const latNum = parseFloat(parsed.lat);
                props.onStreetLatChange(isNaN(latNum) ? null : latNum);
            }
            if (parsed.lng) {
                const lngNum = parseFloat(parsed.lng);
                props.onStreetLngChange(isNaN(lngNum) ? null : lngNum);
            }
        }
    };

    const getStreetViewUrl = () => {
        if (streetViewLat && streetViewLng && heading) {
            const lat = parseFloat(streetViewLat);
            const lng = parseFloat(streetViewLng);
            const head = parseFloat(heading);
            
            if (!isNaN(lat) && !isNaN(lng) && !isNaN(head)) {
                return `https://maps.googleapis.com/maps/api/streetview?size=400x300&location=${lat},${lng}&source=outdoor&heading=${head}&fov=90&key=${config.GOOGLE_API_KEY}`;
            }
        }
        return null;
    };

    const streetViewUrl = getStreetViewUrl();

    return (
        <div>
            <Typography variant="h5" style={{ marginBottom: "24px" }}>
                Location Information
            </Typography>
            
            {loading && (
                <Box display="flex" alignItems="center" style={{ marginBottom: "16px" }}>
                    <CircularProgress size={20} style={{ marginRight: "8px" }} />
                    <Typography variant="body2">Loading location suggestions...</Typography>
                </Box>
            )}

            {error && (
                <Alert severity="error" style={{ marginBottom: "16px" }}>
                    {error}
                </Alert>
            )}

            {suggestions && (
                <Box marginBottom="16px">
                    <Chip 
                        label="✅ Location Data Generated" 
                        size="small" 
                        style={{ 
                            backgroundColor: "#28a745", 
                            color: "white",
                            fontSize: "10px"
                        }} 
                    />
                    <Typography variant="caption" style={{ 
                        color: "#28a745",
                        marginLeft: "8px"
                    }}>
                        BBL: {suggestions.bbl} | Borough: {suggestions.boroughCode} | Block: {suggestions.block} | Lot: {suggestions.lot}
                    </Typography>
                </Box>
            )}

            <Grid container spacing={3} style={{ marginTop: "16px" }}>
                {/* Borough, Block, Lot Fields */}
                <Grid item xs={12}>
                    <div style={{ display: "flex", marginTop: "15px" }}>
                        <div style={{ width: "15%", marginLeft: "10%" }}>
                            <TextField 
                                type="number" 
                                error={props.boroughError} 
                                helperText={props.boroughError ? "**Required" : ""} 
                                style={{ width: "100%" }} 
                                onChange={props.onBoroughChange} 
                                value={props.currentBorough} 
                                id="borough" 
                                label="Borough" 
                            />
                            {suggestions && suggestions.boroughCode && (
                                <Box marginTop="8px">
                                    <Chip 
                                        label="🤖 AI Suggestion" 
                                        size="small" 
                                        style={{ 
                                            backgroundColor: "#28a745", 
                                            color: "white",
                                            fontSize: "10px",
                                            marginBottom: "4px"
                                        }} 
                                    />
                                    <Typography variant="caption" style={{ 
                                        color: "#6c757d",
                                        fontStyle: "italic",
                                        display: "block"
                                    }}>
                                        Suggested: {suggestions.boroughCode}
                                    </Typography>
                                    <Button 
                                        size="small" 
                                        variant="outlined" 
                                        color="primary"
                                        onClick={() => handleApplySuggestion('boroughCode')}
                                        style={{ marginTop: "4px", fontSize: "10px", padding: "2px 8px" }}
                                    >
                                        Apply
                                    </Button>
                                </Box>
                            )}
                        </div>
                        
                        <div style={{ width: "15%", marginLeft: "12px" }}>
                            <TextField 
                                type="number" 
                                error={props.blockError} 
                                helperText={props.blockError ? "**Required" : ""} 
                                style={{ width: "100%" }} 
                                onChange={props.onBlockChange} 
                                value={props.currentBlock} 
                                id="block" 
                                label="Block" 
                            />
                            {suggestions && suggestions.block && (
                                <Box marginTop="8px">
                                    <Chip 
                                        label="🤖 AI Suggestion" 
                                        size="small" 
                                        style={{ 
                                            backgroundColor: "#28a745", 
                                            color: "white",
                                            fontSize: "10px",
                                            marginBottom: "4px"
                                        }} 
                                    />
                                    <Typography variant="caption" style={{ 
                                        color: "#6c757d",
                                        fontStyle: "italic",
                                        display: "block"
                                    }}>
                                        Suggested: {suggestions.block}
                                    </Typography>
                                    <Button 
                                        size="small" 
                                        variant="outlined" 
                                        color="primary"
                                        onClick={() => handleApplySuggestion('block')}
                                        style={{ marginTop: "4px", fontSize: "10px", padding: "2px 8px" }}
                                    >
                                        Apply
                                    </Button>
                                </Box>
                            )}
                        </div>
                        
                        <div style={{ width: "15%", marginLeft: "12px" }}>
                            <TextField 
                                type="number" 
                                error={props.lotError} 
                                helperText={props.lotError ? "**Required" : ""} 
                                style={{ width: "100%" }} 
                                onChange={props.onLotChange} 
                                value={props.currentLot} 
                                id="lot" 
                                label="Lot" 
                            />
                            {suggestions && suggestions.lot && (
                                <Box marginTop="8px">
                                    <Chip 
                                        label="🤖 AI Suggestion" 
                                        size="small" 
                                        style={{ 
                                            backgroundColor: "#28a745", 
                                            color: "white",
                                            fontSize: "10px",
                                            marginBottom: "4px"
                                        }} 
                                    />
                                    <Typography variant="caption" style={{ 
                                        color: "#6c757d",
                                        fontStyle: "italic",
                                        display: "block"
                                    }}>
                                        Suggested: {suggestions.lot}
                                    </Typography>
                                    <Button 
                                        size="small" 
                                        variant="outlined" 
                                        color="primary"
                                        onClick={() => handleApplySuggestion('lot')}
                                        style={{ marginTop: "4px", fontSize: "10px", padding: "2px 8px" }}
                                    >
                                        Apply
                                    </Button>
                                </Box>
                            )}
                        </div>
                    </div>
                </Grid>

                {/* Map Pin Coordinates Section */}
                <Grid item xs={12} md={6}>
                    <Paper style={{ padding: "16px", marginBottom: "16px" }}>
                        <Typography variant="h6" style={{ marginBottom: "12px" }}>
                            Map Pin Coordinates (lat/lng)
                        </Typography>
                        
                        {isEditMode && suggestedLat != null && suggestedLng != null && (
                            <Box marginBottom="12px">
                                <Typography variant="caption" style={{ color: "#6c757d" }}>
                                    Current: {mapPinLat || "N/A"}, {mapPinLng || "N/A"}
                                </Typography>
                                <br />
                                <Typography variant="caption" style={{ color: "#28a745" }}>
                                    Suggested: {suggestedLat}, {suggestedLng}
                                </Typography>
                                <Button 
                                    size="small" 
                                    variant="outlined" 
                                    color="primary"
                                    onClick={handleUseSuggestedCoordinates}
                                    style={{ marginLeft: "8px", fontSize: "10px" }}
                                >
                                    Try Suggested
                                </Button>
                            </Box>
                        )}
                        
                        <div style={{ marginBottom: "12px" }}>
                            <TextField
                                label="Latitude"
                                type="text"
                                value={mapPinLat}
                                onChange={(e) => {
                                    // Allow numbers, decimals, negatives, and empty
                                    const val = e.target.value;
                                    if (val === "" || /^-?\d*\.?\d*$/.test(val)) {
                                        handleMapPinChange('lat', val);
                                    }
                                }}
                                onPaste={(e) => handleMapPinPaste(e, 'lat')}
                                variant="outlined"
                                size="small"
                                fullWidth
                                style={{ marginBottom: "8px" }}
                                placeholder="e.g., 40.7128 or paste: 40.7128, -74.0060"
                                helperText="Paste coordinates from Google Maps (lat, lng)"
                            />
                            <TextField
                                label="Longitude"
                                type="text"
                                value={mapPinLng}
                                onChange={(e) => {
                                    // Allow numbers, decimals, negatives, and empty
                                    const val = e.target.value;
                                    if (val === "" || /^-?\d*\.?\d*$/.test(val)) {
                                        handleMapPinChange('lng', val);
                                    }
                                }}
                                onPaste={(e) => handleMapPinPaste(e, 'lng')}
                                variant="outlined"
                                size="small"
                                fullWidth
                                placeholder="e.g., -74.0060 or paste: 40.7128, -74.0060"
                            />
                        </div>
                        
                        {scriptLoaded && (
                            <div 
                                ref={mapRef} 
                                style={{ height: "300px", width: "100%", border: "1px solid #ccc", borderRadius: "4px" }}
                            />
                        )}
                        {!scriptLoaded && (
                            <Box display="flex" justifyContent="center" alignItems="center" style={{ height: "300px", border: "1px solid #ccc", borderRadius: "4px" }}>
                                <CircularProgress size={24} />
                            </Box>
                        )}
                    </Paper>
                </Grid>

                {/* Street View Coordinates Section */}
                <Grid item xs={12} md={6}>
                    <Paper style={{ padding: "16px", marginBottom: "16px" }}>
                        <Typography variant="h6" style={{ marginBottom: "12px" }}>
                            Street View Coordinates
                        </Typography>
                        
                        <div style={{ marginBottom: "12px" }}>
                            <TextField
                                label="Street View Latitude"
                                type="text"
                                value={streetViewLat}
                                onChange={(e) => {
                                    // Allow numbers, decimals, negatives, and empty
                                    const val = e.target.value;
                                    if (val === "" || /^-?\d*\.?\d*$/.test(val)) {
                                        handleStreetViewChange('lat', val);
                                    }
                                }}
                                onPaste={(e) => handleStreetViewPaste(e, 'lat')}
                                variant="outlined"
                                size="small"
                                fullWidth
                                style={{ marginBottom: "8px" }}
                                placeholder="e.g., 40.7128 or paste: 40.7128, -74.0060"
                                helperText="Paste coordinates from Google Maps (lat, lng)"
                            />
                            <TextField
                                label="Street View Longitude"
                                type="text"
                                value={streetViewLng}
                                onChange={(e) => {
                                    // Allow numbers, decimals, negatives, and empty
                                    const val = e.target.value;
                                    if (val === "" || /^-?\d*\.?\d*$/.test(val)) {
                                        handleStreetViewChange('lng', val);
                                    }
                                }}
                                onPaste={(e) => handleStreetViewPaste(e, 'lng')}
                                variant="outlined"
                                size="small"
                                fullWidth
                                style={{ marginBottom: "8px" }}
                                placeholder="e.g., -74.0060 or paste: 40.7128, -74.0060"
                            />
                            <TextField
                                label="Heading"
                                type="text"
                                value={heading}
                                onChange={(e) => {
                                    // Allow numbers, decimals, and empty
                                    const val = e.target.value;
                                    if (val === "" || /^\d*\.?\d*$/.test(val)) {
                                        handleStreetViewChange('heading', val);
                                    }
                                }}
                                variant="outlined"
                                size="small"
                                fullWidth
                                style={{ marginBottom: "8px" }}
                                placeholder="0-360 (default: 90)"
                            />
                            <Button 
                                size="small" 
                                variant="outlined" 
                                color="primary"
                                onClick={handleSyncToMapPin}
                                style={{ marginTop: "8px" }}
                            >
                                Use Map Pin Coordinates
                            </Button>
                        </div>
                        
                        {streetViewUrl && (
                            <div style={{ marginTop: "12px" }}>
                                <img 
                                    src={streetViewUrl} 
                                    alt="Street View Preview" 
                                    style={{ width: "100%", maxHeight: "300px", borderRadius: "4px" }}
                                />
                            </div>
                        )}
                    </Paper>
                </Grid>
            </Grid>
        </div>
    );
}
